import { readableFileSize } from '@/utilities/utils';
import { template as _template, get as _get } from 'lodash';

export type InterpolationPlaceholders = { [placeholderName: string]: unknown };

const serializeObject = (value: unknown) => {
    if (typeof value === 'object' && value !== null) {
        return JSON.stringify(value, null, 2);
    }
    return String(value);
};

/*
 * Takes the given string and interpolates the provided values to the
 * appropriate placeholders using the template function of lodash.
 * https://lodash.com/docs/4.17.15#template
 *
 * Placeholders are defined using mustache template delimiters:
 * Hello {{ user }}!
 *
 * The imports object contains extra features that can be used in the template:
 * - JSON: The JSON object, which can be used to stringify objects.
 * - serializeObject: A function that serializes an object to a string.
 * - serializeObjectFormatted: A function that serializes an object to a formatted string wrapped in <pre><code> tags.
 */
export function interpolate(
    string: string,
    placeholders: InterpolationPlaceholders = {},
): string {
    const compiled = _template(string, {
        interpolate: /{{([\s\S]+?)}}/g,
        evaluate: /<%([^%>]+)%>/g,
        imports: {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            JSON,
            _get,
            readableFileSize,
            serializeObject: (value: unknown) => serializeObject(value),
            serializeObjectFormatted: (value: unknown) =>
                `<pre><code>${serializeObject(value)}</code></pre>`,
            isDefined: (variableName: string) => {
                return (
                    variableName in placeholders &&
                    typeof placeholders[variableName] !== 'undefined' &&
                    placeholders[variableName] !== null
                );
            },
        },
    });
    try {
        return compiled(placeholders);
    } catch {
        return string;
    }
}
